import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import ModalMobile from "../../../../components/shared/Modal/ModalMobile";
import Button from "../../../../components/shared/Button/Button";
import Divider from "../../../../components/shared/Divider/Divider";
import Select from "../../../../components/shared/Select/Select";
import { adminSearchService } from "../../../../services/adminSearch";
import { useInvalidate } from "../../../../hooks/useInvalidate";
import { useAdminSearchStore } from "../../store/adminSearchStore";
import { useAdminSearchInvites } from "../../hooks/useAdminSearchInvites";
import { STATUS_OPTIONS } from "./InvitesFiltersPanel";
import { toast } from "react-toastify";

const mapInviteToForm = (invite) => ({
  status: invite?.status || "",
});

export default function EditInviteModal({ isOpen, onClose }) {
  const invalidate = useInvalidate();
  const { invitesState } = useAdminSearchStore();
  const { selectedInviteId } = invitesState;
  const { data } = useAdminSearchInvites();
  const invites = data?.invites || [];
  const inviteSummary = useMemo(
    () => invites.find((invite) => invite?.id === selectedInviteId),
    [invites, selectedInviteId],
  );

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { dirtyFields },
  } = useForm({
    defaultValues: mapInviteToForm(inviteSummary),
  });

  useEffect(() => {
    if (inviteSummary) {
      reset(mapInviteToForm(inviteSummary));
    }
  }, [inviteSummary, reset]);

  const mutation = useMutation(async ({ id, data }) => {
    return adminSearchService.adminSearchUpdateInvite({
      id,
      data,
    });
  });

  const statusValue = watch("status");

  const onSubmit = useCallback(
    async (values) => {
      if (!selectedInviteId) return;
      const nextStatus = values.status?.trim() || "";
      if (!dirtyFields?.status) {
        toast.info("No changes to save.");
        return;
      }

      try {
        await mutation.mutateAsync({
          id: selectedInviteId,
          data: { status: nextStatus || null },
        });
        toast.success("Changes saved.");
        invalidate(["adminSearchInvites_key"]);
        onClose?.();
      } catch (err) {
        console.error("Failed to update invite", err);
        toast.error(err?.message || "Failed to update invite");
      }
    },
    [dirtyFields, invalidate, mutation, onClose, selectedInviteId],
  );

  const isSaving = mutation.isPending;

  if (!isOpen || !selectedInviteId) return null;

  return (
    <ModalMobile
      className="font-manrope w-full max-w-xl pt-10"
      isOpen={isOpen}
      onClose={onClose}
      showClose={true}
    >
      <div className="px-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-2xl font-semibold text-gray900">
              Edit invite
            </div>
            <div className="text-base text-gray600">
              {inviteSummary?.invitee || selectedInviteId}
            </div>
          </div>
        </div>

        <Divider className="my-4" color="gray200" type="horizontal" />

        <form className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <label className="text-base text-gray700">Status</label>
            <Select
              options={STATUS_OPTIONS}
              value={statusValue || ""}
              placeholder="Select status"
              withCheckbox
              disabled={isSaving}
              buttonProps={{ size: "md" }}
              onChange={(val) =>
                setValue("status", val || "", {
                  shouldDirty: true,
                  shouldValidate: false,
                })
              }
            />
          </div>
        </form>
      </div>

      <Divider className="mt-6" color="gray200" type="horizontal" />

      <div className="flex items-center justify-between gap-3 px-6 py-4">
        <div className="text-base text-gray600">
          {isSaving && "Saving changes…"}
          {!isSaving && mutation.error
            ? mutation.error?.message || "Failed to update invite"
            : null}
        </div>
        <div className="flex gap-2">
          <Button
            color="gray100"
            onClick={onClose}
            size="md"
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            isLoading={isSaving}
            size="md"
          >
            Save changes
          </Button>
        </div>
      </div>
    </ModalMobile>
  );
}
